import { paletteMode } from "./registry";
import type { AppearanceState } from "./types";

/**
 * Applies an appearance to the document.
 *
 * This is the ONLY place that writes the three attributes at runtime. The inline
 * script in index.html does the same thing once, before first paint; this keeps
 * the document in sync afterwards when the user changes a layer.
 */
export function applyAppearance(appearance: AppearanceState): void {
  const root = document.documentElement;
  root.setAttribute("data-palette", appearance.palette);
  root.setAttribute("data-form", appearance.form);
  root.setAttribute("data-layout", appearance.layout);
  root.style.colorScheme = paletteMode(appearance.palette);
  syncThemeColor();
}

/**
 * Points <meta name="theme-color"> at the palette's own --bg-page so mobile
 * browser chrome and the PWA status bar match the chosen appearance.
 *
 * Read from the computed value rather than duplicated in JS: the palette CSS
 * stays the single source of truth for colour. This runs after stylesheets have
 * loaded, which is fine — theme-color affects browser chrome, not page paint,
 * so it cannot cause a flash of the wrong appearance.
 */
export function syncThemeColor(): void {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue("--bg-page")
    .trim();
  if (!value) return;

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = value;
}
